import React from "react";
import { Form, FloatingLabel, InputGroup, Dropdown, Row } from "react-bootstrap";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";

import { RegisterUIProps } from "./Register.types.tsx";

import { TitleLabel, SubHeaderLabel, HelpLabel } from "../Labels.tsx";
import { ActionButton, ShowPasswordButton } from "../Buttons.tsx";
import {
  CountryDropdownItem,
  CountryDropdownMenu,
  CountryDropdownToggle,
  PhoneInput,
} from "../Inputs.tsx";

import COUNTRIES from "../../constants/countries.tsx";
import { useLoginContext } from "../../views/Login/Login.context.tsx";

const RegisterUI: React.FC<RegisterUIProps> = ({
  formData,
  validated,
  showPassword,
  showConfirmPassword,
  handleShowPassword,
  handleShowConfirmPassword,
  handleSubmit,
  handleChange,
  handleSelected,
  selected,
}) => {
  const { t } = useTranslation();
  const { loadingComponent, setCurrentView, isLoading } = useLoginContext();

  if (isLoading) return loadingComponent;

  return (
    <Row style={{ maxWidth: 400 }} className="mx-auto">
      <TitleLabel>{t("register.title")}</TitleLabel>
      <SubHeaderLabel>{t("register.subtitle")}</SubHeaderLabel>

      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        {/* Email */}
        <FloatingLabel controlId="floatingEmail" label={t("register.emailLabel")} className="mb-3">
          <Form.Control
            type="email"
            name="email"
            placeholder=""
            value={formData.email}
            onChange={handleChange}
            required
          />
          <Form.Control.Feedback type="invalid">{t("register.emailInvalid")}</Form.Control.Feedback>
        </FloatingLabel>

        {/* Full Name */}
        <FloatingLabel
          controlId="floatingName"
          label={t("register.fullNameLabel")}
          className="mb-3"
        >
          <Form.Control
            type="text"
            name="name"
            placeholder=""
            value={formData.name}
            onChange={handleChange}
            required
          />
          <Form.Control.Feedback type="invalid">
            {t("register.fullNameInvalid")}
          </Form.Control.Feedback>
        </FloatingLabel>

        {/* Phone + Country Code */}
        <Form.Group className="mb-3">
          <InputGroup>
            <Dropdown>
              <CountryDropdownToggle variant="light" id="country-dropdown">
                {selected ? selected.code : "+"}
              </CountryDropdownToggle>

              <CountryDropdownMenu>
                {COUNTRIES.map((country) => (
                  <CountryDropdownItem
                    key={country.code}
                    onClick={() => handleSelected(country)}
                    active={selected?.code === country.code}
                  >
                    {country.name} ({country.code})
                  </CountryDropdownItem>
                ))}
              </CountryDropdownMenu>
            </Dropdown>

            <FloatingLabel controlId="floatingPhone" label={t("register.phoneLabel")}>
              <PhoneInput
                type="tel"
                name="phone"
                placeholder=""
                value={formData.phone}
                onChange={handleChange}
                required
                pattern="[0-9]{10}"
              />
              <Form.Control.Feedback type="invalid">
                {t("register.phoneInvalid")}
              </Form.Control.Feedback>
            </FloatingLabel>
          </InputGroup>
        </Form.Group>

        {/* Country */}
        <FloatingLabel
          controlId="floatingCountry"
          label={t("register.countryLabel")}
          className="mb-3"
        >
          <Form.Control
            type="text"
            name="country"
            placeholder=""
            value={formData.country}
            onChange={handleChange}
            required
            disabled
          />
          <Form.Control.Feedback type="invalid">
            {t("register.countryInvalid")}
          </Form.Control.Feedback>
        </FloatingLabel>

        {/* Company */}
        <FloatingLabel
          controlId="floatingCompany"
          label={t("register.companyLabel")}
          className="mb-3"
        >
          <Form.Control
            type="text"
            name="company"
            placeholder=""
            value={formData.company}
            onChange={handleChange}
            required
          />
          <Form.Control.Feedback type="invalid">
            {t("register.companyInvalid")}
          </Form.Control.Feedback>
        </FloatingLabel>

        {/* Password */}
        <InputGroup>
          <FloatingLabel
            controlId="floatingPassword"
            label={t("register.passwordLabel")}
            className="mb-3"
          >
            <Form.Control
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder=""
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
            />
            <Form.Control.Feedback type="invalid">
              {t("register.passwordInvalid")}
            </Form.Control.Feedback>
          </FloatingLabel>

          <ShowPasswordButton role="button" onClick={handleShowPassword}>
            {showPassword ? <EyeSlash /> : <Eye />}
          </ShowPasswordButton>
        </InputGroup>

        {/* Confirm Password */}
        <InputGroup>
          <FloatingLabel
            controlId="floatingConfirmPassword"
            label={t("register.confirmPasswordLabel")}
            className="mb-3"
          >
            <Form.Control
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder=""
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              minLength={6}
              isInvalid={validated && formData.password !== formData.confirmPassword}
            />
            <Form.Control.Feedback type="invalid">
              {formData.password !== formData.confirmPassword
                ? t("register.passwordsDoNotMatch")
                : t("register.confirmPasswordInvalid")}
            </Form.Control.Feedback>
          </FloatingLabel>

          <ShowPasswordButton role="button" onClick={handleShowConfirmPassword}>
            {showConfirmPassword ? <EyeSlash /> : <Eye />}
          </ShowPasswordButton>
        </InputGroup>

        <ActionButton type="submit">{t("register.registerButton")}</ActionButton>

        <HelpLabel onClick={() => setCurrentView("login")}>
          {t("register.alreadyMember")} <b>{t("register.loginAction")}</b>
        </HelpLabel>
      </Form>
    </Row>
  );
};

export default RegisterUI;
